import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  Alert, TextField, InputAdornment, IconButton, Drawer,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button as MuiButton,
  Divider, Grid, Button, Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  InsertDriveFile as CvIcon,
  Block as BlockIcon,
  LockOpen as LockOpenIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useStore from '../../config/store';
import AtsBlockDialog from '../positions/AtsBlockDialog';

const AtsCandidatesLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { project, authToken, organization, user, showAlert } = useStore.getState();

  const isAdminPlus = ['admin', 'admin-grafton', 'super-admin'].includes(user?.rol);
  const isSuperAdmin = user?.rol === 'super-admin';

  const [candidates, setCandidates] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Lista negra (bloqueados) de la organización
  const [blacklist, setBlacklist] = useState([]);
  const [showBlocked, setShowBlocked] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);

  const blockedEmails = new Set(blacklist.map((b) => (b.email || '').toLowerCase()));
  const isBlocked = (email) => blockedEmails.has((email || '').toLowerCase());

  const loadBlacklist = async () => {
    if (!organization) return;
    try {
      const res = await api.get(`/organizations/${organization}/blacklist`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.data.response?.status_response === 'OK') {
        setBlacklist(res.data.response.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Fuera del useEffect: la eliminación en cascada recarga la lista
  const loadCandidates = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(
        `/applicants/candidates-with-applications`,
        {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.response?.status_response === 'OK') {
        setCandidates(response.data.response.data || []);
      } else {
        setError(t('ats.candidates.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('ats.candidates.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project) loadCandidates();
    loadBlacklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFiltered(candidates);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFiltered(
      candidates.filter(
        (c) =>
          c.name?.toLowerCase().includes(term) ||
          c.email?.toLowerCase().includes(term) ||
          c.profession?.toLowerCase().includes(term)
      )
    );
  }, [searchTerm, candidates]);

  const handleRowClick = (candidate) => {
    setSelectedCandidate(candidate);
    setDrawerOpen(true);
  };

  const handleBlock = async (reason) => {
    const c = blockTarget;
    setBlockTarget(null);
    if (!c) return;
    try {
      await api.post(
        `/organizations/${organization}/blacklist`,
        { email: c.email, name: c.name || null, reason: reason || null },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      await loadBlacklist();
      setDrawerOpen(false);
      showAlert(t('ats.detail.blocked'), 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.blockError'), 'error');
    }
  };

  // Eliminación EN CASCADA de un candidato (ficha + postulaciones +
  // conversaciones del bot). Confirmación con Dialog de la app (no alert).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteCandidate = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // OJO: 'uid' de esta lista puede ser el candidateId LEGADO (perfil de
      // evaluaciones) — el id real de la ficha del ATS es 'applicantId'
      const { data } = await api.delete(
        `/applicants/${deleteTarget.applicantId || deleteTarget.uid}/full`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (data.response?.status_response === 'OK') {
        showAlert(t('ats.candidates.deleteSuccess'), 'success');
        setDeleteTarget(null);
        setDrawerOpen(false);
        await loadCandidates();
      } else {
        showAlert(t('ats.candidates.deleteError'), 'error');
      }
    } catch (err) {
      console.error('Error eliminando candidato:', err);
      showAlert(t('ats.candidates.deleteError'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleUnblock = async (email) => {
    try {
      await api.delete(
        `/organizations/${organization}/blacklist/${encodeURIComponent(email || '')}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      await loadBlacklist();
      setDrawerOpen(false);
      showAlert(t('ats.detail.unblocked'), 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.blockError'), 'error');
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleDateString('es-CL');
    if (ts.toDate) return ts.toDate().toLocaleDateString('es-CL');
    const d = new Date(ts);
    return isNaN(d) ? '—' : d.toLocaleDateString('es-CL');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t('ats.candidates.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {showBlocked
              ? `${blacklist.length} ${t('ats.candidates.blockedFound')}`
              : `${filtered.length} ${t('ats.candidates.found')}`}
          </Typography>
        </Box>
        <Button
          variant={showBlocked ? 'contained' : 'outlined'}
          color="error"
          startIcon={<BlockIcon />}
          onClick={() => setShowBlocked((v) => !v)}
          sx={{ flexShrink: 0 }}
        >
          {t('ats.candidates.blocked')} ({blacklist.length})
        </Button>
      </Box>

      {/* Buscador */}
      {!showBlocked && (
        <TextField
          size="small"
          placeholder={t('ats.candidates.search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3, width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}

      {/* Vista lista negra */}
      {showBlocked ? (
        blacklist.length === 0 ? (
          <Alert severity="info">{t('ats.candidates.noBlocked')}</Alert>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('ats.candidates.name')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('ats.detail.blockReason')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('ats.candidates.blockedOn')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{t('ats.positions.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blacklist.map((entry) => (
                  <TableRow key={entry.id || entry.email} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{entry.name || entry.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{entry.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{entry.reason || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatTimestamp(entry.blockedAt)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {isSuperAdmin ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<LockOpenIcon fontSize="small" />}
                          onClick={() => handleUnblock(entry.email)}
                        >
                          {t('ats.detail.unblock')}
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : filtered.length === 0 ? (
        <Alert severity="info">
          {searchTerm ? t('ats.candidates.noResultsSearch') : t('ats.candidates.noResults')}
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.candidates.name')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.candidates.profession')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.candidates.lastPosition')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.candidates.appliedOn')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.candidates.cv')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((candidate) => (
                <TableRow
                  key={candidate.uid || candidate.applicantId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(candidate)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {candidate.name || t('ats.candidates.noName')}
                      </Typography>
                      {isBlocked(candidate.email) && (
                        <Chip
                          label={t('ats.detail.blockedChip')}
                          size="small"
                          sx={{ backgroundColor: '#374151', color: '#fff', height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {candidate.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {candidate.profession || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {candidate.lastApplication?.positionName || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatTimestamp(candidate.lastApplication?.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {candidate.cvUrl ? (
                      <Chip
                        icon={<CvIcon fontSize="small" />}
                        label="CV"
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => window.open(candidate.cvUrl, '_blank')}
                        sx={{ cursor: 'pointer' }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => setDeleteTarget(candidate)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmación de eliminación en cascada (Dialog de la app, no alert) */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('ats.candidates.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ats.candidates.deleteConfirm', { name: deleteTarget?.name || deleteTarget?.email || '' })}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, fontSize: '0.85rem' }}>
            {t('ats.candidates.deleteDetail')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDeleteTarget(null)} color="inherit" disabled={deleting}>
            {t('cancel')}
          </MuiButton>
          <MuiButton
            variant="contained"
            color="error"
            onClick={handleDeleteCandidate}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {t('ats.candidates.deleteGo')}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Drawer detalle candidato */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 420, p: 3 } }}
      >
        {selectedCandidate && (
          <Box>

            {/* Header drawer */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {selectedCandidate.name || t('ats.candidates.noName')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedCandidate.email}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {isBlocked(selectedCandidate.email) ? (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={t('ats.detail.blockedChip')}
                  size="small"
                  sx={{ backgroundColor: '#374151', color: '#fff', fontWeight: 600 }}
                />
                {isSuperAdmin && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<LockOpenIcon fontSize="small" />}
                    onClick={() => handleUnblock(selectedCandidate.email)}
                  >
                    {t('ats.detail.unblock')}
                  </Button>
                )}
              </Box>
            ) : (
              isAdminPlus && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<BlockIcon fontSize="small" />}
                    onClick={() => setBlockTarget(selectedCandidate)}
                    sx={{ borderColor: '#374151 !important', color: '#374151 !important' }}
                  >
                    {t('ats.detail.block')}
                  </Button>
                </Box>
              )
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Perfil */}
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
              {t('ats.candidates.profile')}
            </Typography>

            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {selectedCandidate.profession && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('ats.candidates.profession')}
                  </Typography>
                  <Typography variant="body2">{selectedCandidate.profession}</Typography>
                </Grid>
              )}
              {selectedCandidate.currentPosition && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('ats.candidates.currentPosition')}
                  </Typography>
                  <Typography variant="body2">{selectedCandidate.currentPosition}</Typography>
                </Grid>
              )}
              {selectedCandidate.phone && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('ats.candidates.phone')}
                  </Typography>
                  <Typography variant="body2">{selectedCandidate.phone}</Typography>
                </Grid>
              )}
              {selectedCandidate.country && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('ats.candidates.country')}
                  </Typography>
                  <Typography variant="body2">{selectedCandidate.country}</Typography>
                </Grid>
              )}
              {selectedCandidate.rut && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('ats.candidates.rut')}
                  </Typography>
                  <Typography variant="body2">{selectedCandidate.rut}</Typography>
                </Grid>
              )}
              {selectedCandidate.salarioPretendido && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('ats.candidates.salarioPretendido')}
                  </Typography>
                  <Typography variant="body2">{selectedCandidate.salarioPretendido}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  {t('ats.candidates.registeredOn')}
                </Typography>
                <Typography variant="body2">
                  {formatTimestamp(selectedCandidate.createdAt)}
                </Typography>
              </Grid>
            </Grid>

            {/* CV */}
            {selectedCandidate.cvUrl && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('ats.candidates.cv')}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  href={selectedCandidate.cvUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('ats.candidates.viewCv')}
                </Button>
              </>
            )}

            {/* Última postulación */}
            {selectedCandidate.lastApplication && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('ats.candidates.lastApplicationTitle')}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    border: '1px solid #E5E7EB',
                    borderRadius: 2,
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#F9FAFB' },
                  }}
                  onClick={() => {
                    setDrawerOpen(false);
                    navigate(`/ats/positions/${selectedCandidate.lastApplication.positionId}`);
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedCandidate.lastApplication.positionName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(selectedCandidate.lastApplication.createdAt)}
                      </Typography>
                    </Box>
                    <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </Box>
                </Paper>
              </>
            )}

          </Box>
        )}
      </Drawer>

      <AtsBlockDialog
        open={!!blockTarget}
        onClose={() => setBlockTarget(null)}
        candidateName={blockTarget?.name || blockTarget?.email}
        onConfirm={handleBlock}
      />

    </Box>
  );
};

export default AtsCandidatesLayout;